import { StreamDoc, StreamVariantType, StreamVariant } from './types/streams'
import { UserAgent } from '@sentry/browser/dist/integrations'
import { Cast } from '@/types/casts'

export default {
  hexToRGBA (hex: string): { r: number, g: number, b: number, a: number } {
    let c
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('')
      if (c.length === 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]]
      }
      c = '0x' + c.join('')
      return { r: (c >> 16) & 255, g: (c >> 8) & 255, b: c & 255, a: 255 }
    }
    return { r: 0, g: 0, b: 0, a: 0 }
  },
  getStreamForVideo (video: StreamDoc, quality: string = 'lowrate'): string {
    let url = ''
    if (video.hasOwnProperty('variants')) {
      if (video.variants.hasOwnProperty(quality)) {
        url = video.variants[quality].url
      } else {
        for (const key in video.variants) {
          if (video.variants[key].hasOwnProperty('url')) {
            url = video.variants[key].url
          }
        }
      }
    }
    const urlSegments = url.split('/')
    return urlSegments[urlSegments.length - 1]
  },
  getVariant (streamdoc: StreamDoc, varianttype: StreamVariantType, transcodingserver: string[]): StreamVariant | null {
    const allowedTypes = ['original', 'lowratenosrt', 'lowratecaster']
    if (streamdoc !== undefined && streamdoc.variants !== undefined) {
      for (const variantid in streamdoc.variants) {
        const variant = streamdoc.variants[variantid]
        if (variant.hasOwnProperty('type') && variant.type === varianttype) {
          if (allowedTypes.includes(varianttype) || transcodingserver.includes(variant.server)) {
            return variant
          }
        }
      }
    }
    return null
  },
  getRandomInt (min: number, max: number): number {
    const rmin = Math.ceil(min)
    const rmax = Math.floor(max)
    const val = Math.floor(Math.random() * (rmax - rmin)) + rmin
    return val
  },
  makeId (): string {
    return this.generateRandomString(12)
  },
  compareDicts (obj1: any, obj2: any): boolean {
    for (const p in obj1) {
      // Check property exists on both objects
      if (obj1.hasOwnProperty(p) !== obj2.hasOwnProperty(p)) return false
      switch (typeof (obj1[p])) {
        // Deep compare objects
        case 'object':
          if (!this.compareDicts(obj1[p], obj2[p])) return false
          break
        // Compare function code
        case 'function':
          if (typeof (obj2[p]) === 'undefined' || (p !== 'compare' && obj1[p].toString() !== obj2[p].toString())) return false
          break
        // Compare values
        default:
          if (obj1[p] !== obj2[p]) return false
      }
    }
    // Check object 2 for any extra properties
    for (const p in obj2) {
      if (typeof (obj1[p]) === 'undefined') return false
    }
    return true
  },
  getAgent (): UserAgent {
    return {
      // @ts-ignore
      browser_codename: navigator.appCodeName,
      browser: navigator.appName,
      cookies_enabled: navigator.cookieEnabled,
      browser_language: navigator.language,
      platform: navigator.platform,
      agent_header: navigator.userAgent,
      is_chrome: window.hasOwnProperty('chrome'),
      chrome_version: this.chromeVersion(),
      screen: {
        width: screen.width,
        height: screen.height,
        colors: screen.colorDepth
      },
      timezone: this.getTimezone(),
      cpus: navigator.hardwareConcurrency,
      // @ts-ignore
      memory: navigator.deviceMemory === undefined ? 0 : navigator.deviceMemory
    }
  },
  chromeVersion (): { major: number, minor: number, build: number, patch: number } | undefined {
    const pieces = navigator.userAgent.match(/Chrom(?:e|ium)\/([0-9]+)\.([0-9]+)\.([0-9]+)\.([0-9]+)/)
    if (pieces === null || pieces.length !== 5) {
      return undefined
    }
    const piecesNumbers = pieces.map(piece => parseInt(piece, 10))
    return {
      major: piecesNumbers[1],
      minor: piecesNumbers[2],
      build: piecesNumbers[3],
      patch: piecesNumbers[4]
    }
  },
  getTimezone () {
    const timezone = (new Date()).getTimezoneOffset() / 60
    if (timezone > 0) {
      return 'GMT -' + timezone
    } else {
      return 'GMT +' + (-timezone)
    }
  },
  androidVersion (ua) {
    const agent = (ua || navigator.userAgent).toLowerCase()
    let match = agent.match(/android\s([0-9\.]*)/)  // eslint-disable-line
    if (!match) {
      return 0
    }
    const l = match[1].split('.')
    return [parseInt(l[0], 10), parseInt(l[1], 10), parseInt(l[2] || 0, 10)]
  },
  mos2score (mos) {
    // according to https://www.voipfuture.com/wordpress/wp-content/uploads/2015/10/VPF123_WP_MOS-Calculation-And-Aggregation.pdf
    if (mos > 4.34) {
      return 5
    } else if (mos > 4.03) {
      return 4
    } else if (mos > 3.6) {
      return 3
    } else if (mos > 3.1) {
      return 2
    } else if (mos > 2.58) {
      return 1
    }
    return 0
  },
  getNetworkScore (latency, jitter, packetslost = 0) {
    const effectiveLatency = latency + 2 * jitter + 10.0
    let R = 0
    if (effectiveLatency < 160.0) {
      R = 93.2 - (effectiveLatency) / 40.0
    } else {
      R = 93.2 - (effectiveLatency - 120.0) / 10.0
    }
    R = R - 2.5 * packetslost
    let mos = 0
    if (R < 0) {
      mos = 1.0
    } else if (R < 100) {
      mos = 1 + 0.035 * R + 0.000007 * R * (R - 60) * (100 - R)
    } else {
      mos = 4.5
    }
    return this.mos2score(mos) || 0
  },
  isMac (): boolean {
    return navigator.platform.toUpperCase().indexOf('MAC') >= 0
  },
  initialCaps (s: string): string {
    if (typeof s !== 'string') return ''
    return s.charAt(0).toUpperCase() + s.slice(1)
  },
  scaleVals (num: number, in_min: number, in_max: number, out_min: number, out_max: number): number  {
    return (num - in_min) * (out_max - out_min) / (in_max - in_min) + out_min
  },
  isActiveCast (cast: Partial<Cast> | undefined): boolean {
    if (!cast || !cast.start_date || !cast.end_date) return false
    const now = new Date()
    const startDate = (cast.start_date.seconds) ? new Date(cast.start_date.seconds * 1000) : cast.start_date
    const endDate = (cast.end_date.seconds) ? new Date(cast.end_date.seconds * 1000) : cast.end_date
    return now.getTime() >= startDate.getTime() && now.getTime() <= endDate.getTime()
  },
  getURL (router, name, params): string {
    const route = router?.resolve({ name, params })
    return route?.href ? route.href : ''
  },
  generateRandomString (length: number = 16, includeNumbers: boolean = true): string {
    let chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
    if (includeNumbers) chars += '0123456789'

    let result = ''

    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }

    return result
  },
  getAtLeastStartDate (obj: { start_date: any }, dateNow?: Date): Date {
    let endDate = dateNow ?? new Date()
    const startDate = obj.start_date.seconds !== undefined ? new Date(obj.start_date.seconds * 1000) : obj.start_date
    if (startDate > endDate) endDate = startDate
    return endDate
  },
  getProgressText (progress: number|undefined): string {
    if (progress === undefined) return 'In Queue'
    return `${progress.toFixed(1)}%`
  },
  replaceUrlVersion (url: string, newVersion: string): string {
    try {
      const urlObj = new URL(url)
      if (urlObj.hostname === 'localhost') return url

      let currentVersion = ''
      // '/3.8.0/events' -> ['', '3.8.0', 'events']
      const pathParts = urlObj.pathname.split('/')
      if (pathParts.length >= 2 && pathParts[1].match(/^\d+\.(?:\d+\.)+\d+$/)) {
        currentVersion = pathParts[1]
      }

      if (currentVersion === '' && newVersion !== '') {
        urlObj.pathname = `/${newVersion}${urlObj.pathname}`
      } else if (currentVersion !== '' && newVersion === '') {
        urlObj.pathname = urlObj.pathname.replace(`/${currentVersion}`, '')
      } else if (currentVersion !== newVersion) {
        urlObj.pathname = urlObj.pathname.replace(currentVersion, newVersion)
      }

      return urlObj.toString()
    } catch {
      return ''
    }
  }
}
